"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { fadeUp } from "@/lib/motion";
import { useSnapCarousel } from "@/lib/useSnapCarousel";
import CarouselDots from "./CarouselDots";

const testimonials = [
  {
    name: "Alfonso Workman",
    role: "CTO at Meridian Analytics",
    quote: "AgencyDB cut our data sourcing time by 73%. The API is incredibly clean.",
    image: "https://i.pravatar.cc/150?u=alfonso",
  },
  {
    name: "Charlie Geidt",
    role: "CTO at Meridian Analytics",
    quote:
      "Lorem ipsum dolor sit amet consectetur. Duis est dapibus sed odio sed fermentum. Vitae tincidunt volutpat eget proin nisi. Pellentesque commodo lectus in nisi.",
    image: "https://i.pravatar.cc/150?u=charlie",
  },
  {
    name: "Ashlynn George",
    role: "CTO at Meridian Analytics",
    quote:
      "Lorem ipsum dolor sit amet consectetur. Nisl diam est nibh egestas nunc egestas lorem consectetur. Sed ultricies aliquam.",
    image: "https://i.pravatar.cc/150?u=ashlynn",
  },
  {
    name: "Lincoln Philips",
    role: "CTO at Meridian Analytics",
    quote: "AgencyDB cut our data sourcing time by 73%. The API is incredibly clean.",
    image: "https://i.pravatar.cc/150?u=lincoln",
  },
];

const VISIBLE = 3;

function TestimonialCard({ t }: { t: (typeof testimonials)[number] }) {
  return (
    <div className="flex h-full flex-col justify-between rounded-[18px] border border-black/10 bg-white p-5 shadow-sm sm:min-h-70 sm:rounded-3xl sm:p-8">
      <div>
        <div className="flex gap-1 text-brand">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="h-3.5 w-3.5 fill-brand text-brand sm:h-4 sm:w-4" />
          ))}
        </div>
        <div className="mt-2 font-serif text-3xl leading-none text-black/10 sm:mt-6 sm:text-5xl">
          “
        </div>
        <p className="mt-1 text-sm leading-relaxed text-ink-soft sm:mt-2">
          {t.quote}
        </p>
      </div>
      <div className="mt-4 flex items-center gap-3 sm:mt-8">
        {t.image ? (
          <img
            src={t.image}
            alt={t.name}
            className="h-10 w-10 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cream-dark text-sm font-semibold text-brand">
            {t.name.charAt(0)}
          </span>
        )}
        <div>
          <p className="text-sm font-bold text-ink">{t.name}</p>
          <p className="text-xs text-ink-soft/80">{t.role}</p>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const {
    containerRef,
    setItemRef,
    active,
    scrollTo,
  } = useSnapCarousel(testimonials.length);

  const next = () => {
    const nextIdx = (index + 1) % testimonials.length;
    setIndex(nextIdx);
    scrollTo(nextIdx);
  };
  const prev = () => {
    const prevIdx = (index - 1 + testimonials.length) % testimonials.length;
    setIndex(prevIdx);
    scrollTo(prevIdx);
  };

  const visible = Array.from({ length: VISIBLE }, (_, i) => {
    const t = testimonials[(index + i) % testimonials.length];
    return { ...t, key: `${index}-${i}` };
  });

  return (
    <section className="bg-[#E86C260F] py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-4xl font-medium tracking-tight text-ink sm:text-5xl leading-[1.1]"
          >
            Trusted by data-driven teams
          </motion.h2>
          <div className="flex w-full justify-end gap-4 sm:w-auto">
            <button
              onClick={prev}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-brand/30 text-brand/50 transition-all hover:border-brand hover:text-brand"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-5 w-5 stroke-[1.5]" />
            </button>
            <button
              onClick={next}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-brand text-brand transition-all hover:bg-brand/5"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-5 w-5 stroke-[1.5]" />
            </button>
          </div>
        </div>

        {/* Mobile carousel (peeking cards) */}
        <div className="mt-10 sm:hidden">
          <div
            ref={containerRef}
            className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-[4%] pb-2 scrollbar-none"
          >
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                ref={setItemRef(i)}
                className="w-[85%] shrink-0 snap-center"
              >
                <TestimonialCard t={t} />
              </div>
            ))}
          </div>
          <CarouselDots
            count={testimonials.length}
            active={active}
            onSelect={scrollTo}
          />
        </div>

        {/* Desktop grid with arrow-driven window */}
        <div className="mt-16 hidden gap-6 sm:grid sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((t) => (
              <motion.div
                key={t.key}
                layout
                initial={{ opacity: 0, x: 50, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -50, scale: 0.95 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="w-full"
              >
                <TestimonialCard t={t} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
